import React, { useState, useRef, useEffect } from 'react';
import {
  Bell, X, ThumbsUp, MessageCircle, BookOpen,
  CheckCheck, Users, Megaphone,
} from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';

// ---------------------------------------------------------------------------
// Notification type → icon + label mapping
// Mirrors the NOTIFICATION_TYPES constant from useNotifications
// ---------------------------------------------------------------------------
const TYPE_CONFIG = {
  postLiked:          { icon: ThumbsUp,      label: 'liked your post' },
  postCommented:      { icon: MessageCircle,  label: 'commented on your post' },
  commentLiked:       { icon: ThumbsUp,      label: 'liked your comment' },
  commentReplied:     { icon: MessageCircle,  label: 'replied to your comment' },
  recipeLiked:        { icon: BookOpen,       label: 'liked your recipe' },
  recipeCommented:    { icon: BookOpen,       label: 'commented on your recipe' },
  communityApproved:  { icon: Users,          label: 'Your community was approved' },
  communityRejected:  { icon: Users,          label: 'Your community was rejected' },
  mentioned:          { icon: MessageCircle,  label: 'mentioned you' },
  announcement:       { icon: Megaphone,      label: 'Announcement' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const timeAgo = (date) => {
  if (!date) return '';
  const parsed = date?.toDate ? date.toDate() : new Date(date);
  if (isNaN(parsed.getTime())) return '';
  const seconds = Math.floor((new Date() - parsed) / 1000);
  if (seconds < 60)    return 'just now';
  if (seconds < 3600)  return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

// ---------------------------------------------------------------------------
// NotificationRow — single notification item
// ---------------------------------------------------------------------------
const NotificationRow = ({ notification, onDelete }) => {
  const config = TYPE_CONFIG[notification.type] || { icon: Bell, label: notification.type };
  const Icon = config.icon;

  return (
    <div
      className={`flex items-start gap-md px-lg py-md transition-colors ${
        !notification.isRead ? 'bg-primary/4' : 'hover:bg-primary/3'
      }`}
    >
      {/* Icon */}
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mt-xs">
        <Icon size={14} className="text-primary" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-caption text-text-primary">
          {notification.actorUserName && (
            <span className="font-semibold">{notification.actorUserName} </span>
          )}
          {config.label}
          {notification.communityName && (
            <span className="text-text-secondary"> in {notification.communityName}</span>
          )}
        </p>
        {notification.message && (
          <p className="text-xs text-text-secondary mt-xs line-clamp-2">
            {notification.message}
          </p>
        )}
        <p className="text-xs text-text-tertiary mt-xs">{timeAgo(notification.timestamp)}</p>
      </div>

      {/* Unread dot */}
      {!notification.isRead && (
        <div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-sm" />
      )}

      {/* Delete */}
      <button
        onClick={() => onDelete(notification.id)}
        className="flex-shrink-0 p-xs rounded-full text-text-tertiary hover:text-primary hover:bg-primary/8 transition-all"
        aria-label="Dismiss notification"
      >
        <X size={12} />
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// NotificationBell
//
// Self-contained bell icon + dropdown panel.
// Intended to be placed in the app header.
// ---------------------------------------------------------------------------
const NotificationBell = () => {
  const { notifications, unreadCount, loading, markAllAsRead, deleteNotification } =
    useNotifications(30);

  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  // -------------------------------------------------------------------------
  // Close on outside click
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Mark all read when panel opens
  const handleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && unreadCount > 0) {
      markAllAsRead().catch(() => {});
    }
  };

  const handleDelete = (id) => {
    deleteNotification(id).catch(() => {});
  };

  return (
    <div className="relative">

      {/* Bell button */}
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className="relative p-sm rounded-full text-text-secondary hover:text-primary hover:bg-primary/8 transition-all"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-xs rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 top-full mt-sm w-[360px] max-h-[480px] rounded-large bg-surface shadow-lg overflow-hidden z-50 flex flex-col"
          style={{ border: '1px solid rgba(var(--color-text-tertiary),0.12)' }}
        >

          {/* Header */}
          <div className="flex items-center justify-between px-lg py-md border-b border-text-tertiary/10 flex-shrink-0">
            <h3 className="text-callout font-semibold text-text-primary">Notifications</h3>
            {notifications.length > 0 && (
              <button
                onClick={() => markAllAsRead().catch(() => {})}
                className="flex items-center gap-xs text-xs font-semibold text-primary hover:text-primary/70 transition-colors"
              >
                <CheckCheck size={13} />
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1" style={{ scrollbarWidth: 'none' }}>
            {loading ? (
              <div className="flex justify-center py-xl">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-xxxl text-center px-lg">
                <Bell size={32} className="text-text-tertiary mb-md" />
                <p className="text-caption font-semibold text-text-primary mb-xs">
                  No notifications
                </p>
                <p className="text-xs text-text-tertiary">
                  You are all caught up
                </p>
              </div>
            ) : (
              <div className="divide-y divide-text-tertiary/8">
                {notifications.map(n => (
                  <NotificationRow
                    key={n.id}
                    notification={n}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default NotificationBell;
